import { useState } from 'react';
import Button from 'antd/lib/button';
import Space from 'antd/lib/space';
import Spin from 'antd/lib/spin';
import Empty from 'antd/lib/empty';
import Typography from 'antd/lib/typography';
import DownloadOutlined from '@ant-design/icons/lib/icons/DownloadOutlined';
import FileTextOutlined from '@ant-design/icons/lib/icons/FileTextOutlined';
import { BILL_BORDER, BILL_PDF_BACKDROP } from './billStyles';

const { Text } = Typography;

const ZOOM_MIN = 1;
const ZOOM_MAX = 2.5;
const ZOOM_STEP = 0.25;

interface Props {
    // Pre-signed S3 URL; `null` means the backend has no PDF for the record (404).
    pdfUrl: string | null | undefined;
    isLoading: boolean;
    isError: boolean;
    onDownload: () => void;
    isDownloading: boolean;
    title?: string;
    errorText?: string;
    emptyText?: string;
}

// Shared PDF viewer for the bill / bill-file modals. Data fetching lives in the thin wrappers (BillPdfPanel,
// BillFilePdfPanel); this component only renders.
//
// The document renders in the browser's built-in viewer (an <iframe>), so we carry no PDF library or worker.
// We drive zoom ourselves by widening the iframe (the document is fit-to-width, so a wider frame = a larger
// page) inside a normal scrollable container, rather than using the viewer's own zoom: the embedded viewer
// centers the page and clips its left edge when its internal zoom overflows a narrow frame, whereas our
// left-aligned overflow container scrolls both ways correctly. The viewer's own toolbar is hidden (#toolbar=0).
export const PdfPreviewPanel = ({
    pdfUrl,
    isLoading,
    isError,
    onDownload,
    isDownloading,
    title = 'Utility Bill PDF',
    errorText = 'Could not load the PDF',
    emptyText = 'No PDF available',
}: Props) => {
    const [zoom, setZoom] = useState(ZOOM_MIN);

    const zoomOut = () => setZoom(z => Math.max(ZOOM_MIN, +(z - ZOOM_STEP).toFixed(2)));
    const zoomIn = () => setZoom(z => Math.min(ZOOM_MAX, +(z + ZOOM_STEP).toFixed(2)));

    return (
        <div
            style={{
                width: '58%',
                minWidth: 0,
                background: BILL_PDF_BACKDROP,
                borderRight: `1px solid ${BILL_BORDER}`,
                display: 'flex',
                flexDirection: 'column',
            }}
        >
            <div
                style={{
                    padding: '8px 16px',
                    background: '#fff',
                    borderBottom: `1px solid ${BILL_BORDER}`,
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                }}
            >
                <Space>
                    <FileTextOutlined />
                    <Text strong style={{ fontSize: 13 }}>
                        {title}
                    </Text>
                </Space>
                <Space size={4}>
                    <Button size="small" aria-label="Zoom out" onClick={zoomOut} disabled={!pdfUrl || zoom <= ZOOM_MIN}>
                        −
                    </Button>
                    <Text style={{ fontSize: 12, width: 40, textAlign: 'center', display: 'inline-block' }}>{Math.round(zoom * 100)}%</Text>
                    <Button size="small" aria-label="Zoom in" onClick={zoomIn} disabled={!pdfUrl || zoom >= ZOOM_MAX}>
                        +
                    </Button>
                    <Button size="small" icon={<DownloadOutlined />} onClick={onDownload} loading={isDownloading} disabled={!pdfUrl}>
                        Download
                    </Button>
                </Space>
            </div>
            <div style={{ flex: 1, overflow: 'auto', display: 'flex' }}>
                {isLoading ? (
                    <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <Spin tip="Loading PDF…" />
                    </div>
                ) : isError ? (
                    <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <Empty description={errorText} />
                    </div>
                ) : !pdfUrl ? (
                    <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <Empty description={emptyText} />
                    </div>
                ) : (
                    <iframe
                        src={`${pdfUrl}#toolbar=0&view=FitH`}
                        title={title}
                        // flexShrink:0 so the zoom width (>100%) isn't shrunk back to the flex container by the
                        // flex algorithm — it must overflow for the scrollable container to zoom the fit-to-width page.
                        style={{ display: 'block', width: `${zoom * 100}%`, height: '100%', border: 'none', flexShrink: 0 }}
                    />
                )}
            </div>
        </div>
    );
};
